import { signOut, throwIfFronteggFailed } from "@/lib/auth/auth.utils";
import { ORG_SELECTED_KEY } from "@/modules/tenants/components/org-chooser/org-selected-key";
import {
  useAuth,
  useTenantsActions,
} from "@frontegg/react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  AUTH_ROLES_QUERY_KEY,
  AUTH_USERS_QUERY_KEY,
  buildIdentityHeaders,
  fetchFronteggRolesCatalog,
  fetchUsersRolesForIds,
  getIdentityBaseUrl,
  parseJsonResponse,
  useIdentityAuth,
} from "./users.client";
import {
  assertAssignableRoleIds,
  parseFronteggRolesPayload,
  sanitizeRoleAssignmentDiff,
  toAuthRolePickerOptions,
  type AuthActorRoleRef,
} from "./users.roles";
import {
  AUTH_USERS_PAGE_SIZE,
  getAuthUsersTotalPages,
  mergeUsersWithRoleDetails,
  parseFronteggUsersPagePayload,
} from "./users.utils";

export {
  AUTH_ROLES_QUERY_KEY,
  AUTH_USERS_QUERY_KEY,
} from "./users.client";

export {
  AUTH_USERS_PAGE_SIZE,
  AUTH_USERS_PAGINATION_THRESHOLD,
  getAuthUsersPageRange,
  getAuthUsersTotalPages,
  mapFronteggUserRow,
  mergeUsersWithRoleDetails,
  mergeUsersWithRoles,
  parseFronteggUsersPagePayload,
  parseFronteggUsersPayload,
  parseFronteggUsersRolesPayload,
  pinCurrentUserFirst,
  resolveRolesFromCatalog,
  type FronteggIdentityRole,
  type FronteggIdentityUser,
  type FronteggUserRoleAssignment,
  type FronteggUsersPage,
} from "./users.utils";

export {
  assertAssignableRoleIds,
  diffRoleIds,
  getActorMaxRoleLevel,
  isRoleAssignable,
  parseFronteggRolesPayload,
  sanitizeRoleAssignmentDiff,
  toAuthRolePickerOptions,
  type AuthActorRoleRef,
  type AuthRolePickerOption,
} from "./users.roles";

// =============================================================================
// /identity/resources/users/v3
// =============================================================================

/**
 * Lists tenant users. `offset` is Frontegg's 0-based **page index** (not item skip):
 * page 1 → 0, page 2 → 1, with `_limit` as page size.
 */
export function useAuthUsers(options?: {
  email?: string;
  enabled?: boolean;
  limit?: number;
  offset?: number;
}) {
  const { token, tenantId } = useIdentityAuth();
  const email = options?.email?.trim() || undefined;
  const enabled = options?.enabled ?? true;
  const limit = options?.limit ?? AUTH_USERS_PAGE_SIZE;
  const offset = options?.offset ?? 0;

  return useQuery({
    enabled: Boolean(token) && enabled,
    queryFn: async () => {
      const params = new URLSearchParams({
        _limit: String(limit),
        _offset: String(offset),
      });
      if (email) {
        params.set("_email", email);
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/users/v3?${params}`,
        { headers: buildIdentityHeaders(token!, tenantId) },
      );
      await throwIfFronteggFailed(response, "Frontegg users request failed");
      const json: unknown = await response.json();
      const page = parseFronteggUsersPagePayload(json, tenantId, limit, offset);
      if (page.users.length === 0) {
        return page;
      }
      const userIds = page.users.map((user) => user.id);
      const detailedUsers = await fetchUsersRolesForIds(
        token!,
        tenantId,
        userIds,
      );
      return {
        ...page,
        users: mergeUsersWithRoleDetails(page.users, detailedUsers),
      };
    },
    queryKey: [
      ...AUTH_USERS_QUERY_KEY,
      { email: email ?? null, limit, offset, tenantId },
    ],
  });
}

export function useInvalidateAuthUsers() {
  const queryClient = useQueryClient();

  return function invalidateAuthUsers() {
    return queryClient.invalidateQueries({ queryKey: AUTH_USERS_QUERY_KEY });
  };
}

export function useAuthUsersWithPagination(options?: {
  email?: string;
  enabled?: boolean;
}) {
  const email = options?.email?.trim() || undefined;
  const filterSignature = email ?? "";
  const [page, setPage] = useState(1);
  const [prevFilterSignature, setPrevFilterSignature] =
    useState(filterSignature);

  let pageForQuery = page;
  if (prevFilterSignature !== filterSignature) {
    setPrevFilterSignature(filterSignature);
    setPage(1);
    pageForQuery = 1;
  }

  // Frontegg `_offset` is a 0-based page index, not (page - 1) * limit.
  const offset = pageForQuery - 1;
  const queryResult = useAuthUsers({
    email,
    enabled: options?.enabled,
    limit: AUTH_USERS_PAGE_SIZE,
    offset,
  });

  const limit = queryResult.data?.limit ?? AUTH_USERS_PAGE_SIZE;
  const totalItems = queryResult.data?.totalItems ?? 0;
  const totalPages =
    queryResult.data?.totalPages ?? getAuthUsersTotalPages(totalItems, limit);
  const canPrevious = pageForQuery > 1 && totalPages > 0;
  const canNext = pageForQuery < totalPages;

  return Object.assign({}, queryResult, {
    canNext,
    canPrevious,
    limit,
    page: pageForQuery,
    setPage,
    totalItems,
    totalPages,
    users: queryResult.data?.users ?? [],
  });
}

// =============================================================================
// /identity/resources/roles/v1
// =============================================================================

export function useAuthRoles(options?: { enabled?: boolean }) {
  const { token, tenantId } = useIdentityAuth();
  const enabled = options?.enabled ?? true;

  return useQuery({
    enabled: Boolean(token) && enabled,
    queryFn: async () => {
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/roles/v1`,
        { headers: buildIdentityHeaders(token!, tenantId) },
      );
      await throwIfFronteggFailed(response, "Frontegg roles request failed");
      const json: unknown = await response.json();
      return parseFronteggRolesPayload(json);
    },
    queryKey: [...AUTH_ROLES_QUERY_KEY, { tenantId }],
  });
}

export function useInvalidateAuthRoles() {
  const queryClient = useQueryClient();

  return function invalidateAuthRoles() {
    return queryClient.invalidateQueries({ queryKey: AUTH_ROLES_QUERY_KEY });
  };
}

/**
 * Role checkbox options filtered by Frontegg hierarchy for the signed-in user.
 * Pass `preserveRoleIds` on edit so above-actor roles stay visible but disabled.
 */
export function useAuthRolePickerOptions(options?: {
  enabled?: boolean;
  preserveRoleIds?: string[];
}) {
  const { user } = useAuth();
  const rolesQuery = useAuthRoles({ enabled: options?.enabled });
  const preserveKey = (options?.preserveRoleIds ?? []).join(",");

  const pickerOptions = useMemo(
    function buildAuthRolePickerOptions() {
      if (!rolesQuery.data) {
        return [];
      }
      return toAuthRolePickerOptions(
        rolesQuery.data,
        (user?.roles ?? []) as AuthActorRoleRef[],
        {
          preserveRoleIds:
            preserveKey.length > 0 ? preserveKey.split(",") : [],
        },
      );
    },
    [preserveKey, rolesQuery.data, user?.roles],
  );

  const assignableCount = pickerOptions.filter(
    (option) => option.assignable,
  ).length;

  return {
    ...rolesQuery,
    assignableCount,
    options: pickerOptions,
  };
}

// =============================================================================
// /identity/resources/users/v2 (invite)
// =============================================================================

type InviteUserInput = {
  email: string;
  name?: string;
  roleIds: string[];
};

export function useInviteUser(
  options?: UseMutationOptions<unknown, Error, InviteUserInput>,
) {
  const invalidateAuthUsers = useInvalidateAuthUsers();
  const { token, tenantId, actorRoles } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async (input: InviteUserInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const catalog = await fetchFronteggRolesCatalog(token, tenantId);
      const roleIds = assertAssignableRoleIds(
        catalog,
        actorRoles,
        input.roleIds,
      );
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/users/v2`,
        {
          body: JSON.stringify({
            email: input.email,
            name: input.name || undefined,
            roleIds,
            metadata: "{}",
          }),
          headers: {
            ...buildIdentityHeaders(token, tenantId),
            "Content-Type": "application/json",
          },
          method: "POST",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg invite failed");
      return parseJsonResponse(response);
    },
    onSuccess: async (...args) => {
      await invalidateAuthUsers();
      await options?.onSuccess?.(...args);
    },
  });
}

// =============================================================================
// /identity/resources/users/v1/{userId}/roles
// =============================================================================

type UpdateUserRolesInput = {
  userId: string;
  nextRoleIds: string[];
  currentRoleIds: string[];
};

export function useUpdateUserRoles(
  options?: UseMutationOptions<unknown, Error, UpdateUserRolesInput>,
) {
  const invalidateAuthUsers = useInvalidateAuthUsers();
  const { token, tenantId, actorRoles } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async (input: UpdateUserRolesInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const catalog = await fetchFronteggRolesCatalog(token, tenantId);
      const { addRoleIds, removeRoleIds } = sanitizeRoleAssignmentDiff({
        catalog,
        actorRoles,
        nextRoleIds: input.nextRoleIds,
        currentRoleIds: input.currentRoleIds,
      });
      const headers = {
        ...buildIdentityHeaders(token, tenantId),
        "Content-Type": "application/json",
      };
      const rolesUrl = `${getIdentityBaseUrl()}/resources/users/v1/${input.userId}/roles`;

      if (removeRoleIds.length > 0) {
        const response = await fetch(rolesUrl, {
          body: JSON.stringify({ roleIds: removeRoleIds }),
          headers,
          method: "DELETE",
        });
        await throwIfFronteggFailed(
          response,
          "Frontegg unassign roles failed",
        );
      }
      if (addRoleIds.length > 0) {
        const response = await fetch(rolesUrl, {
          body: JSON.stringify({ roleIds: addRoleIds }),
          headers,
          method: "POST",
        });
        await throwIfFronteggFailed(response, "Frontegg assign roles failed");
      }
      return null;
    },
    onSuccess: async (...args) => {
      await invalidateAuthUsers();
      await options?.onSuccess?.(...args);
    },
  });
}

// =============================================================================
// /identity/resources/tenants/users/v1/{userId}/disable|enable
// =============================================================================

type UserIdInput = { userId: string };

export function useDisableUser(
  options?: UseMutationOptions<unknown, Error, UserIdInput>,
) {
  const invalidateAuthUsers = useInvalidateAuthUsers();
  const { token, tenantId } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async ({ userId }: UserIdInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/tenants/users/v1/${userId}/disable`,
        {
          headers: buildIdentityHeaders(token, tenantId),
          method: "POST",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg disable user failed");
      return parseJsonResponse(response);
    },
    onSuccess: async (...args) => {
      await invalidateAuthUsers();
      await options?.onSuccess?.(...args);
    },
  });
}

export function useEnableUser(
  options?: UseMutationOptions<unknown, Error, UserIdInput>,
) {
  const invalidateAuthUsers = useInvalidateAuthUsers();
  const { token, tenantId } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async ({ userId }: UserIdInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/tenants/users/v1/${userId}/enable`,
        {
          headers: buildIdentityHeaders(token, tenantId),
          method: "POST",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg enable user failed");
      return parseJsonResponse(response);
    },
    onSuccess: async (...args) => {
      await invalidateAuthUsers();
      await options?.onSuccess?.(...args);
    },
  });
}

// =============================================================================
// /identity/resources/users/sessions/v1/me/all
// =============================================================================

export function useLogoutUserSessions(
  options?: UseMutationOptions<unknown, Error, UserIdInput>,
) {
  const { token } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async ({ userId }: UserIdInput) => {
      if (!token) {
        throw new Error("Missing auth token");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/users/sessions/v1/me/all`,
        {
          headers: {
            ...buildIdentityHeaders(token),
            "frontegg-user-id": userId,
          },
          method: "DELETE",
        },
      );
      await throwIfFronteggFailed(
        response,
        "Frontegg logout sessions failed",
      );
      return parseJsonResponse(response);
    },
  });
}

// =============================================================================
// /identity/resources/users/v1/{userId}
// =============================================================================

export function useDeleteUser(
  options?: UseMutationOptions<unknown, Error, UserIdInput>,
) {
  const invalidateAuthUsers = useInvalidateAuthUsers();
  const { token, tenantId } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async ({ userId }: UserIdInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/users/v1/${userId}`,
        {
          headers: buildIdentityHeaders(token, tenantId),
          method: "DELETE",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg delete user failed");
      return parseJsonResponse(response);
    },
    onSuccess: async (...args) => {
      await invalidateAuthUsers();
      await options?.onSuccess?.(...args);
    },
  });
}

export type LeaveOrganizationResult =
  | { kind: "logout" }
  | { kind: "switch"; tenantId: string };

const fronteggMeTenantSchema = z
  .object({
    name: z.string().optional(),
    tenantId: z.string(),
  })
  .passthrough();

function parseFronteggMeTenantsPayload(json: unknown): { tenantId: string }[] {
  let rows: unknown[] = [];
  if (Array.isArray(json)) {
    rows = json;
  } else if (json && typeof json === "object") {
    const root = json as Record<string, unknown>;
    const nested = root.items ?? root.tenants ?? root.data;
    if (Array.isArray(nested)) {
      rows = nested;
    }
  }

  return rows.flatMap((row) => {
    const parsed = fronteggMeTenantSchema.safeParse(row);
    return parsed.success ? [{ tenantId: parsed.data.tenantId }] : [];
  });
}

/**
 * Removes the signed-in user from the active tenant, then switches to another
 * tenant when one remains, or signs out when this was their last organization.
 */
export function useLeaveOrganization(
  options?: UseMutationOptions<LeaveOrganizationResult, Error, void>,
) {
  const { token, tenantId, userId } = useIdentityAuth();
  const { switchTenant } = useTenantsActions();

  return useMutation({
    ...options,
    mutationFn: async () => {
      if (!token || !userId || !tenantId) {
        throw new Error("Missing auth context for leave organization");
      }

      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/users/v1/${userId}`,
        {
          headers: buildIdentityHeaders(token, tenantId),
          method: "DELETE",
        },
      );
      await throwIfFronteggFailed(
        response,
        "Frontegg leave organization failed",
      );

      const tenantsResponse = await fetch(
        `${getIdentityBaseUrl()}/resources/users/v2/me/tenants`,
        { headers: buildIdentityHeaders(token) },
      );
      await throwIfFronteggFailed(
        tenantsResponse,
        "Frontegg list tenants after leave failed",
      );
      const tenantsJson: unknown = await tenantsResponse.json();
      const remaining = parseFronteggMeTenantsPayload(tenantsJson).filter(
        (item) => item.tenantId !== tenantId,
      );

      if (remaining.length === 0) {
        return { kind: "logout" as const };
      }
      return { kind: "switch" as const, tenantId: remaining[0]!.tenantId };
    },
    onSuccess: async (...args) => {
      const [result] = args;
      sessionStorage.removeItem(ORG_SELECTED_KEY);
      if (result.kind === "logout") {
        signOut();
      } else {
        await switchTenant({
          tenantId: result.tenantId,
          silentReload: true,
        });
        window.location.assign("/");
      }
      await options?.onSuccess?.(...args);
    },
  });
}
